import type { ReactNode } from "react";
import React, { useMemo } from "react";
import { useLocation } from "react-router-dom";

import {
  Box,
  Button,
  Tab,
  Tabs,
  Typography,
} from "@braintrust/braintrust-ui-components";
import { useAccountType } from "@js/apps/common/hooks";
import { useShouldDisplayL1RoleTab } from "@js/apps/common/hooks";
import {
  CreateJobModalInstance,
  openCreateJobModal,
} from "@js/apps/employer/components/create-job-modal";
import { useCanManageJobsOnBehalfOfClient } from "@js/apps/jobs/hooks";
import { RouterLink } from "@js/components/link";
import { useAppSelector } from "@js/hooks";
import type { Freelancer } from "@js/types/freelancer";
import { removeTrailingSlash } from "@js/utils";

import style from "./style.module.scss";

const JOBS_TAB = {
  path: `/jobs`,
  label: "All jobs",
};
type HeaderProps = {
  filtersSocket?: ReactNode;
  buttons?: React.ReactNode[];
};

export const Header = ({ buttons, filtersSocket }: HeaderProps) => {
  const { pathname } = useLocation();
  const canManageJobAsCoreMember = useCanManageJobsOnBehalfOfClient();
  const { isFreelancer } = useAccountType();
  const freelancerProfile = useAppSelector(
    (state) => state.freelancer.freelancerProfile,
  );
  const shouldDisplayL1Tab = useShouldDisplayL1RoleTab();

  const jobTabs = useMemo(() => {
    const roleTab =
      isFreelancer && freelancerProfile && getRoleTab(freelancerProfile);
    return shouldDisplayL1Tab && roleTab ? [roleTab, JOBS_TAB] : [JOBS_TAB];
  }, [freelancerProfile, isFreelancer, shouldDisplayL1Tab]);

  const tabValue = useMemo(() => {
    const normalizedPathname = removeTrailingSlash(pathname);
    const current = jobTabs.find((tab) => tab.path === normalizedPathname);

    return current?.path ?? JOBS_TAB.path;
  }, [pathname, jobTabs]);

  return (
    <Box className={style.jobListingHeader}>
      {canManageJobAsCoreMember && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            mb: 2,
            mt: 2,
          }}
        >
          <Button
            shape="squared"
            size="medium"
            variant="positive"
            onClick={openCreateJobModal}
          >
            Post Job for a Client
          </Button>
          <CreateJobModalInstance />
        </Box>
      )}
      <Box className={style.jobListingHeaderTopSection}>
        <Tabs
          aria-label="job tabs"
          value={tabValue}
          variant="scrollable"
          allowScrollButtonsMobile
          sx={{
            "& .MuiTabs-flexContainer": {
              gap: {
                xs: "16px",
                sm: "40px",
              },
            },
          }}
        >
          {jobTabs.map(({ path, label }) => (
            <Tab
              key={label}
              value={path}
              inactiveColorVariable="grey-3"
              component={RouterLink}
              to={path}
              sx={{ pb: 1 }}
              label={
                <Typography
                  component="span"
                  variant="title"
                  size="medium"
                  fontWeight={400}
                >
                  {label}
                </Typography>
              }
            />
          ))}
        </Tabs>
        <Box className={style.jobListingHeaderButtons}>{buttons}</Box>
      </Box>

      <Box className={style.jobListingHeaderSearchSection}>{filtersSocket}</Box>
    </Box>
  );
};

const getRoleTab = (profile: Freelancer) => {
  const selectedRole = profile.role;
  if (!selectedRole) {
    return;
  }

  const jobLabel = selectedRole.name;

  return {
    path: `/role-jobs`,
    label: `${jobLabel} jobs`,
  };
};
